import pino from "pino"

export interface LoggerConfig {
    level?: string
    allowedTags?: string[]
    minLevel?: number
    serverEndpoint?: string
    enableServerLogging?: boolean
}

export class FilteredLogger {
    private logger: pino.Logger
    private config: LoggerConfig

    constructor(config: LoggerConfig = {}) {
        this.config = {
            level: config.level || "info",
            allowedTags: config.allowedTags || [],
            minLevel: config.minLevel || 30, // 30 = info level
            serverEndpoint: config.serverEndpoint || "http://localhost:8080/log",
            enableServerLogging: config.enableServerLogging || false,
        }

        // Check if we're in a browser environment
        const isBrowser = typeof window !== "undefined"

        if (isBrowser) {
            // Browser environment - use basic pino without transport
            this.logger = pino({
                level: this.config.level,
                browser: {
                    asObject: false,
                    write: (o: any) => {
                        // Custom pretty printing for browser
                        const level = o.level
                        const date = new Date(o.time)
                        const time =
                            date.toTimeString().split(" ")[0] + "." + date.getMilliseconds().toString().padStart(3, "0")
                        const msg = o.msg || ""
                        const tags = o.tags ? ` [${o.tags.join(", ")}]` : ""
                        const extra = Object.keys(o)
                            .filter(k => !["level", "time", "msg", "tags"].includes(k))
                            .map(k => `${k}: ${JSON.stringify(o[k])}`)
                            .join(" ")

                        const levelColors = {
                            10: "\x1b[90m", // trace - gray
                            20: "\x1b[36m", // debug - cyan
                            30: "\x1b[32m", // info - green
                            40: "\x1b[33m", // warn - yellow
                            50: "\x1b[31m", // error - red
                            60: "\x1b[35m", // fatal - magenta
                        }

                        const levelNames = {
                            10: "TRACE",
                            20: "DEBUG",
                            30: "INFO",
                            40: "WARN",
                            50: "ERROR",
                            60: "FATAL",
                        }

                        const color = levelColors[level as keyof typeof levelColors] || ""
                        const reset = "\x1b[0m"
                        const levelName = levelNames[level as keyof typeof levelNames] || "LOG"

                        console.log(`${color}[${time}]${reset} ${msg}${extra ? " " + extra : ""}`)
                    },
                },
            })
        } else {
            // Node.js environment - use pino-pretty
            this.logger = pino({
                level: this.config.level,
                transport: {
                    target: "pino-pretty",
                    options: {
                        colorize: true,
                        translateTime: "HH:MM:ss.SSS",
                        ignore: "pid,hostname",
                        singleLine: false,
                        hideObject: false,
                    },
                },
            })
        }

        // Output configuration on initialization
        this.logConfiguration()
    }

    // Log current configuration
    private logConfiguration() {
        const tagFilter =
            this.config.allowedTags!.length > 0
                ? `Tags: [${this.config.allowedTags!.join(", ")}] (OR logic)`
                : "Tags: All (no filtering)"

        const levelFilter = `Level: ${this.config.level} (min: ${this.config.minLevel})`

        console.log(`🔧 Logger initialized - ${levelFilter}, ${tagFilter}`)
    }

    // Update configuration at runtime
    updateConfig(newConfig: Partial<LoggerConfig>) {
        this.config = { ...this.config, ...newConfig }

        // Update logger level if changed
        if (newConfig.level) {
            this.logger.level = newConfig.level
        }

        // Log updated configuration
        this.logConfiguration()
    }

    // Convenience method to update only the allowed tags
    setAllowedTags(tags: string[]) {
        this.updateConfig({ allowedTags: tags })
    }

    // Convenience method to add tags to the current allowed tags
    addAllowedTags(tags: string[]) {
        const currentTags = this.config.allowedTags || []
        const newTags = [...new Set([...currentTags, ...tags])] // Remove duplicates
        this.updateConfig({ allowedTags: newTags })
    }

    // Convenience method to remove tags from the current allowed tags
    removeAllowedTags(tags: string[]) {
        const currentTags = this.config.allowedTags || []
        const newTags = currentTags.filter(tag => !tags.includes(tag))
        this.updateConfig({ allowedTags: newTags })
    }

    // Convenience method to clear all tag filtering (allow all logs)
    clearTagFilter() {
        this.updateConfig({ allowedTags: [] })
    }

    // Convenience method to enable/disable server logging
    setServerLogging(enabled: boolean, endpoint?: string) {
        this.updateConfig({
            enableServerLogging: enabled,
            serverEndpoint: endpoint || this.config.serverEndpoint,
        })
    }

    // Get current configuration
    getConfig(): LoggerConfig {
        return { ...this.config }
    }

    private shouldLog(level: number, logData?: any): boolean {
        // Check if log level meets minimum requirement
        if (level < this.config.minLevel!) {
            return false
        }

        // If no tags are specified, allow all logs
        if (this.config.allowedTags!.length === 0) {
            return true
        }

        // Check if the log has tags and if ANY of them are in allowedTags
        if (typeof logData === "object" && logData && logData.tags && Array.isArray(logData.tags)) {
            // Check if any log tag is in the allowed tags
            return logData.tags.some(tag => this.config.allowedTags!.includes(tag))
        } else {
            // If no tags in the log but tags are required, don't log
            return false
        }
    }

    // Log methods that match pino's API
    trace(obj?: any, msg?: string, ...args: any[]) {
        if (this.shouldLog(10, obj)) {
            this.logger.trace(obj, msg, ...args)
            // Send to server if enabled
            if (this.config.enableServerLogging) {
                const tags = obj?.tags
                const extra = { ...obj }
                delete extra.tags
                this.sendToServer("trace", msg || "", tags, extra)
            }
        }
    }

    debug(obj?: any, msg?: string, ...args: any[]) {
        if (this.shouldLog(20, obj)) {
            this.logger.debug(obj, msg, ...args)
            // Send to server if enabled
            if (this.config.enableServerLogging) {
                const tags = obj?.tags
                const extra = { ...obj }
                delete extra.tags
                this.sendToServer("debug", msg || "", tags, extra)
            }
        }
    }

    info(obj?: any, msg?: string, ...args: any[]) {
        if (this.shouldLog(30, obj)) {
            this.logger.info(obj, msg, ...args)
            // Send to server if enabled
            if (this.config.enableServerLogging) {
                const tags = obj?.tags
                const extra = { ...obj }
                delete extra.tags
                this.sendToServer("info", msg || "", tags, extra)
            }
        }
    }

    warn(obj?: any, msg?: string, ...args: any[]) {
        if (this.shouldLog(40, obj)) {
            this.logger.warn(obj, msg, ...args)
            // Send to server if enabled
            if (this.config.enableServerLogging) {
                const tags = obj?.tags
                const extra = { ...obj }
                delete extra.tags
                this.sendToServer("warn", msg || "", tags, extra)
            }
        }
    }

    error(obj?: any, msg?: string, ...args: any[]) {
        if (this.shouldLog(50, obj)) {
            this.logger.error(obj, msg, ...args)
            // Send to server if enabled
            if (this.config.enableServerLogging) {
                const tags = obj?.tags
                const extra = { ...obj }
                delete extra.tags
                this.sendToServer("error", msg || "", tags, extra)
            }
        }
    }

    fatal(obj?: any, msg?: string, ...args: any[]) {
        if (this.shouldLog(60, obj)) {
            this.logger.fatal(obj, msg, ...args)
            // Send to server if enabled
            if (this.config.enableServerLogging) {
                const tags = obj?.tags
                const extra = { ...obj }
                delete extra.tags
                this.sendToServer("fatal", msg || "", tags, extra)
            }
        }
    }

    // Send log to server endpoint
    private async sendToServer(level: string, msg: string, tags?: string[], extra?: any) {
        if (!this.config.enableServerLogging || !this.config.serverEndpoint) {
            return
        }

        try {
            const logData = {
                level,
                msg,
                tags: tags || [],
                timestamp: new Date().toISOString(),
                extra,
            }

            await fetch(this.config.serverEndpoint!, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(logData),
            })
        } catch (error) {
            // Silently fail to avoid infinite logging loops
            console.warn("Failed to send log to server:", error)
        }
    }

    // Convenience method for simple string logging
    log(msg: string, tags?: string[]) {
        if (tags) {
            this.info({ tags }, msg)
        } else {
            this.info(msg)
        }
    }
}

// Create a default logger instance
export const logger = new FilteredLogger({
    allowedTags: ["viewport", "callback", "animation", "map", "damage"],
    level: "debug",
    minLevel: 10, // 10 = trace level, allow all logs
    enableServerLogging: false, // Enable server logging for damage debugging
    serverEndpoint: "http://localhost:8080/log",
})
